import React, { useState } from 'react';
import { VerisureTechnician } from '../../types';
import { toast } from 'react-hot-toast';

export interface TechnicianProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  technician: VerisureTechnician | null;
  onSelectEquipment?: (itemId: string) => void;
}

export const TechnicianProfileModal: React.FC<TechnicianProfileModalProps> = ({
  isOpen,
  onClose,
  technician,
  onSelectEquipment,
}) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'equipment' | 'reviews'>('overview');
  const [bookingMessage, setBookingMessage] = useState('');
  const [isSubmittingBooking, setIsSubmittingBooking] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);

  if (!isOpen || !technician) return null;

  const handleBookingSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmittingBooking(true);
    setTimeout(() => {
      setIsSubmittingBooking(false);
      setBookingSuccess(true);
      toast.success(`Service request sent to ${technician.name}!`);
    }, 800);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto">
      <div className="relative max-w-2xl w-full my-8 bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-2xl overflow-hidden font-sans">
        {/* Background Glow */}
        <div className="absolute -top-10 -right-10 w-60 h-60 bg-orange-500/10 rounded-full blur-3xl pointer-events-none"></div>

        {/* Close Button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white w-9 h-9 rounded-full flex items-center justify-center text-xl font-bold transition border border-slate-700 z-10"
        >
          &times;
        </button>

        {/* Header Profile Info */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 pb-6 border-b border-slate-800">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-tr from-red-600 via-orange-500 to-amber-500 p-0.5 shadow-lg shadow-orange-500/20 shrink-0">
            <div className="w-full h-full bg-slate-950 rounded-2xl flex items-center justify-center text-2xl font-black text-white">
              {technician.name.charAt(0)}
            </div>
          </div>

          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-2xl font-black text-white">{technician.name}</h2>
              <span className="bg-gradient-to-r from-red-500/15 to-orange-500/15 border border-orange-500/30 text-orange-400 text-[10px] font-extrabold px-2.5 py-0.5 rounded-full uppercase tracking-wider flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                Certified Technician
              </span>
            </div>
            <p className="text-slate-400 text-xs mt-0.5">
              {technician.certificationLevel || 'Master Certified Specialist (Level 4)'} • {technician.location || 'Lagos, Nigeria'}
            </p>

            <div className="flex flex-wrap items-center gap-4 mt-3 text-xs">
              <div className="flex items-center gap-1 text-amber-400 font-bold">
                <span>★</span> {technician.rating || 4.9} ({technician.reviewCount || 34} Reviews)
              </div>
              <div className="text-slate-400">
                <span className="text-white font-bold">{technician.jobsCompleted || 42}</span> Verified Repairs
              </div>
              <div className="text-emerald-400 font-medium">
                Avg. Response: {technician.responseTime || '< 2 Hours'}
              </div>
            </div>
          </div>
        </div>

        {/* Insurance Claims Evidence Callout */}
        <div className="my-5 bg-gradient-to-r from-slate-950 via-slate-900 to-slate-950 border border-orange-500/20 rounded-2xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-orange-500/10 border border-orange-500/30 flex items-center justify-center text-orange-400 shrink-0">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
              </svg>
            </div>
            <div>
              <div className="text-xs font-bold text-white">Insurer-Ready Maintenance Evidence</div>
              <div className="text-[11px] text-slate-400">
                All repairs include tamper-proof photographic forensics & diagnostic telemetry for seamless insurance claim filing
              </div>
            </div>
          </div>
          <span className="bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider whitespace-nowrap">
            Audit Ready
          </span>
        </div>

        {/* Navigation Tabs */}
        <div className="flex gap-2 border-b border-slate-800 pb-3 mb-5">
          <button
            type="button"
            onClick={() => setActiveTab('overview')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition ${
              activeTab === 'overview'
                ? 'bg-gradient-to-r from-red-600 to-orange-500 text-white shadow-md shadow-orange-500/20'
                : 'text-slate-400 hover:text-white bg-slate-800/60'
            }`}
          >
            Overview & Booking
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('equipment')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition flex items-center gap-1.5 ${
              activeTab === 'equipment'
                ? 'bg-gradient-to-r from-red-600 to-orange-500 text-white shadow-md shadow-orange-500/20'
                : 'text-slate-400 hover:text-white bg-slate-800/60'
            }`}
          >
            Maintained Equipment ({technician.maintainedEquipments?.length || 5})
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('reviews')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition flex items-center gap-1.5 ${
              activeTab === 'reviews'
                ? 'bg-gradient-to-r from-red-600 to-orange-500 text-white shadow-md shadow-orange-500/20'
                : 'text-slate-400 hover:text-white bg-slate-800/60'
            }`}
          >
            Owner Reviews ({technician.reviews?.length || 4})
          </button>
        </div>

        {/* Tab 1: Overview & Booking */}
        {activeTab === 'overview' && (
          <div className="space-y-5">
            <div>
              <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Specializations</h4>
              <div className="flex flex-wrap gap-2">
                {(technician.specializations || ['Industrial Equipment', 'Consumer Electronics', 'HVAC & Generators', 'Fiber Optics']).map((spec, idx) => (
                  <span key={idx} className="bg-slate-800 border border-slate-700 text-slate-300 text-xs font-semibold px-3 py-1 rounded-lg">
                    {spec}
                  </span>
                ))}
              </div>
            </div>

            {technician.bio && (
              <div>
                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">About the Technician</h4>
                <p className="text-sm text-slate-300 leading-relaxed bg-slate-950/60 p-3 rounded-xl border border-slate-800">
                  {technician.bio}
                </p>
              </div>
            )}

            {/* Quick Service Request Form */}
            <div className="bg-slate-950 border border-slate-800 p-4 rounded-2xl">
              <h4 className="text-sm font-bold text-white mb-2 flex items-center gap-2">
                Request Repair / Inspection Service
              </h4>

              {bookingSuccess ? (
                <div className="p-4 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 rounded-xl text-xs">
                  <strong>Request Submitted!</strong> {technician.name} will contact you shortly to confirm schedule and inspect equipment.
                </div>
              ) : (
                <form onSubmit={handleBookingSubmit} className="space-y-3">
                  <textarea
                    rows={2}
                    value={bookingMessage}
                    onChange={(e) => setBookingMessage(e.target.value)}
                    placeholder="Describe your equipment issue or maintenance requirement..."
                    className="w-full bg-slate-900 border border-slate-700 text-white rounded-xl px-3 py-2 text-xs focus:outline-none focus:border-orange-500"
                    required
                  />
                  <div className="flex justify-end">
                    <button
                      type="submit"
                      disabled={isSubmittingBooking}
                      className="bg-gradient-to-r from-red-600 to-orange-500 hover:opacity-90 text-white text-xs font-bold px-5 py-2.5 rounded-xl transition shadow-md shadow-orange-500/20 disabled:opacity-50"
                    >
                      {isSubmittingBooking ? 'Sending...' : 'Send Service Request'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}

        {/* Tab 2: Maintained Equipment */}
        {activeTab === 'equipment' && (
          <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
            <p className="text-xs text-slate-400 mb-2">
              Other equipment regularly serviced by {technician.name} across the Verisure network:
            </p>
            {(technician.maintainedEquipments || [
              { itemId: 'ITM-1029', assetId: 'VRS-1029', make: 'Fujikura', model: '90S+ Splicer', category: 'consumer_electronics', repairsCount: 3, conditionScore: 92, lastServicedDate: '2026-08-14' },
              { itemId: 'ITM-4032', assetId: 'VRS-4032', make: 'Caterpillar', model: 'DE110E2 Genset', category: 'industrial', repairsCount: 5, conditionScore: 88, lastServicedDate: '2026-07-29' },
              { itemId: 'ITM-7718', assetId: 'VRS-7718', make: 'Schneider', model: 'MasterPact MTZ ACB', category: 'electrical', repairsCount: 2, conditionScore: 95, lastServicedDate: '2026-08-01' },
              { itemId: 'ITM-5521', assetId: 'VRS-5521', make: 'Daikin', model: 'VRV-IV Air Handler', category: 'home_appliance', repairsCount: 4, conditionScore: 86, lastServicedDate: '2026-06-20' },
            ]).map((eq, idx) => (
              <div
                key={idx}
                className="bg-slate-950 border border-slate-800 p-3.5 rounded-2xl flex items-center justify-between gap-3 hover:border-orange-500/40 transition"
              >
                <div>
                  <div className="font-bold text-white text-sm">
                    {eq.make} {eq.model}
                  </div>
                  <div className="text-[11px] text-slate-400 font-mono mt-0.5">
                    Asset: <span className="text-orange-400">{eq.assetId}</span> • {eq.repairsCount} Verified Services
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <div className="text-xs font-bold text-emerald-400">{eq.conditionScore}/100</div>
                    <div className="text-[10px] text-slate-500">Condition</div>
                  </div>
                  {onSelectEquipment && (
                    <button
                      type="button"
                      onClick={() => {
                        onClose();
                        onSelectEquipment(eq.itemId);
                      }}
                      className="bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs px-2.5 py-1.5 rounded-lg border border-slate-700"
                    >
                      View &rarr;
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Tab 3: Owner Reviews */}
        {activeTab === 'reviews' && (
          <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
            {(technician.reviews || [
              { id: '1', ownerName: 'Chidi Okonkwo (Facility Director)', rating: 5, comment: 'Exceptional precision on our industrial generator overhaul. Replaced the AVR with genuine OEM parts. Highly recommended.', date: '2026-08-15', itemTitle: 'CAT DE110E2' },
              { id: '2', ownerName: 'Amara Bello', rating: 5, comment: 'Calibrated our fiber optical fusion splicer within 2 hours. Logged the immutable repair instantly on Verisure.', date: '2026-08-02', itemTitle: 'Fujikura 90S+' },
              { id: '3', ownerName: 'Tunde Bakare', rating: 5, comment: 'Very professional. The detailed photographic logs made filing our insurer breakdown claim effortless.', date: '2026-07-20', itemTitle: 'Schneider ACB' },
              { id: '4', ownerName: 'Grace E.', rating: 4.8, comment: 'Timely service and comprehensive diagnostic report provided after compressor repair.', date: '2026-06-25', itemTitle: 'Daikin VRV-IV' },
            ]).map((rev) => (
              <div key={rev.id} className="bg-slate-950 border border-slate-800 p-4 rounded-2xl">
                <div className="flex justify-between items-start gap-2 mb-1.5">
                  <div>
                    <span className="font-bold text-white text-xs">{rev.ownerName}</span>
                    {rev.itemTitle && <span className="text-slate-500 text-[11px]"> • {rev.itemTitle}</span>}
                  </div>
                  <div className="flex items-center text-amber-400 text-xs font-bold">
                    {'★'.repeat(Math.round(rev.rating))}
                    <span className="text-slate-400 text-[10px] ml-1">{rev.rating}</span>
                  </div>
                </div>
                <p className="text-slate-300 text-xs leading-relaxed italic">"{rev.comment}"</p>
                <div className="text-[10px] text-slate-500 mt-2 font-mono">{rev.date} • Verified Owner Review</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
